import { useMemo } from "react";

import {
  CREATE_MMM_CUSTOM_SCENARIO_DATA,
  DELETE_MMM_CUSTOM_SCENARIO_DATA,
  FETCH_CUSTOM_SCENARIO_DATA,
  UPDATE_MMM_CUSTOM_SCENARIO_DATA,
} from "@gql/mmm-scenario-modeling";
import { useNorthbeamMutation, useNorthbeamQuery } from "@hooks/http";
import {
  type UpdateMmmCustomScenarioData,
  type UpdateMmmCustomScenarioDataVariables,
  type CreateMmmCustomScenarioData,
  type CreateMmmCustomScenarioDataVariables,
  type FetchCustomScenarioData,
  type FetchCustomScenarioDataVariables,
  type DeleteMmmCustomScenarioData,
  type DeleteMmmCustomScenarioDataVariables,
} from "@north-beam/nb-client-side";
import { useSelectedCycle } from "@pages/mmm/hooks/use-selected-cycle";

export const useCustomScenarioData = () => {
  const { selectedCycle } = useSelectedCycle();
  const { data, loading: customScenarioDataLoading } = useNorthbeamQuery<
    FetchCustomScenarioData,
    FetchCustomScenarioDataVariables
  >(FETCH_CUSTOM_SCENARIO_DATA, {
    variables: { cycleId: selectedCycle.id },
  });

  const customScenarioData = useMemo(
    () => data?.me.mmm.customScenarioData ?? [],
    [data],
  );

  const [
    createCustomScenarioData,
    { loading: isCreateCustomScenarioDataLoading },
  ] = useNorthbeamMutation<
    CreateMmmCustomScenarioData,
    CreateMmmCustomScenarioDataVariables
  >(CREATE_MMM_CUSTOM_SCENARIO_DATA, {
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: FETCH_CUSTOM_SCENARIO_DATA,
        variables: { cycleId: selectedCycle.id },
      },
    ],
  });

  const [updateCustomScenarioData, { loading: isUpdateCustomScenarioLoading }] =
    useNorthbeamMutation<
      UpdateMmmCustomScenarioData,
      UpdateMmmCustomScenarioDataVariables
    >(UPDATE_MMM_CUSTOM_SCENARIO_DATA, {
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: FETCH_CUSTOM_SCENARIO_DATA,
          variables: {
            cycleId: selectedCycle.id,
          },
        },
      ],
    });

  const [deleteCustomScenarioData, { loading: isDeleteCustomScenarioLoading }] =
    useNorthbeamMutation<
      DeleteMmmCustomScenarioData,
      DeleteMmmCustomScenarioDataVariables
    >(DELETE_MMM_CUSTOM_SCENARIO_DATA, {
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: FETCH_CUSTOM_SCENARIO_DATA,
          variables: {
            cycleId: selectedCycle.id,
          },
        },
      ],
    });

  return {
    customScenarioData,
    customScenarioDataLoading,
    createCustomScenarioData,
    isCreateCustomScenarioDataLoading,
    updateCustomScenarioData,
    isUpdateCustomScenarioLoading,
    deleteCustomScenarioData,
    isDeleteCustomScenarioLoading,
  };
};
